import { Position, Toaster } from "@blueprintjs/core";
import { configureStore } from "@reduxjs/toolkit";
import { combineEpics, createEpicMiddleware } from "redux-observable";
import _ajax, { authErrorEpic } from "../helpers/ajaxWrapper";
import { cdmDuck } from "../services";
import {
  availableConjunctionHeaders,
  conjunctionColumnsLayout,
  fleetConjunctionsColumnsLayout,
  orbitColumnsLayout,
  orbitConjunctionsColumnsLayout,
  plotsLayout,
  postOpmConjunctionsColumnsLayout,
} from "../services/configuration";
import {
  conjCDMDuck,
  conjOPMDuck,
  conjunctionDetail,
  conjunctions,
  fleetConjunctions,
} from "../services/conjunctions";
import { createUploadOrbit } from "../services/downloaduploadOrbits";
import { grafosConfig, grafosDat } from "../services/grafos";
import { orbitConjunctions, orbitDetail, orbits } from "../services/orbits";
import { plots } from "../services/plots";
import { opm, opmConjunctions } from "../services/postcam";
import createServerConfiguration from "../services/serverConfig/configuration";
import apiKeys from "./apiKeysDuck";
import { appThemeConfigSlice } from "./appConfigSlice";
import authSlice from "./authSlice";
import bahnPlotDuck from "./bahnPlotDuck";
import catalogueDuck from "./catalogueDuck";
import eventsEvolutionDuck from "./eventsEvolutionDuck";
import evolutionPlotDuck from "./EvolutionPlotDuck";
import { createReducer, getMiddlewares } from "./helpers";
import phonebookSlice from "./phonebookDuck";
import releaseNotesDuck from "./releaseNotesDuck";
import systemConfig from "./systemConfigurationDuck";
import toasterDuck from "./toasterDuck";
import userControllerSlice from "./userControllerDuck";

const serverConfig = createServerConfiguration();
const uploadedOrbits = createUploadOrbit();
const toaster = Toaster.create({ position: Position.BOTTOM_RIGHT });

const ducks = {
  auth: authSlice,
  appThemeConfig: appThemeConfigSlice,
  conjunctions,
  opm,
  opmConjunctions,
  conjunctionDetail,
  grafosConfig,
  grafosDat,
  bahnPlot: bahnPlotDuck,
  evolutionPlot: evolutionPlotDuck,
  plots,
  orbits,
  orbitDetail,
  orbitConjunctions,
  uploadedOrbits,
  toaster: toasterDuck,
  availableConjunctionHeaders,
  conjunctionColumnsLayout,
  orbitColumnsLayout,
  orbitConjunctionsColumnsLayout,
  fleetConjunctionsColumnsLayout,
  postOpmConjunctionsColumnsLayout,
  plotsLayout,
  serverConfig,
  fleetConjunctions,
  cdm: cdmDuck,
  conjunctionCDM: conjCDMDuck,
  conjunctionOPM: conjOPMDuck,
  phonebook: phonebookSlice,
  systemConfig,
  apiKeys,
  userController: userControllerSlice,
  releaseNotes: releaseNotesDuck,
  catalogue: catalogueDuck,
  eventsEvolution: eventsEvolutionDuck,
};

// Combina con otros reducers que tengas (auth, etc)
const rootReducer = createReducer(ducks);

const epics = [authErrorEpic];

const duckEpics = Object.values(ducks).flatMap((duck) => getMiddlewares(duck));

const allEpics = [...epics, ...duckEpics];

// Combina todos los epics de cada duck en uno solo
const rootEpic = combineEpics(...allEpics);

// Inyectamos dependencias
const epicMiddleware = createEpicMiddleware({
  dependencies: { ajax: _ajax(), toaster },
});

// Configuras la store con el rootReducer
export const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    // Desactiva thunk porque usas epics
    getDefaultMiddleware({ thunk: false }).concat(epicMiddleware),
});

// Ejecuta el rootEpic
epicMiddleware.run(rootEpic);

// Tipos para usar en useSelector y useDispatch
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
